/**
 * useFieldHiddenFields (Unit 4 Phase 1) — per-site hidden-column state for
 * the field records screen.
 *
 * THE FIRST persisted per-user UI preference in the field zone. Every other
 * zone already keeps this class of state in localStorage (useGridState.js for
 * the desktop grids, CustomizeViewDrawer for inventory); the field zone had
 * exactly none — `fieldView` (INV/ALL) is session-only ON PURPOSE
 * (FieldZonePlaceholder:66-67, "a rep's next shift starts on INV"). This one
 * persists deliberately and for a different reason: INV/ALL is a two-state
 * switch a rep re-flips in one tap, while a hidden-column set is a dozen
 * decisions that must survive the tab eviction a phone performs between rack
 * aisles.
 *
 * STORAGE SHAPE — one object per site, sized for Unit 5:
 *   key    toolsight-field-fsg-<siteId>
 *   value  { v: 1, hiddenByView: { inv: ['cspc', ...], all: ['oem', ...] },
 *            sort: [...], filter: [...], filterLogic, group }
 *
 * PER-VIEW (P1-2 piece 3). Each view keeps its own hidden set under
 * hiddenByView[view]: hiding a key in INV leaves ALL alone, and the reverse.
 * The hook is handed the view ('inv' | 'all'). With no view, or any other
 * value, the hidden set is empty and every mutator is inert — there is no
 * default view.
 *
 * hiddenFields IS DEAD DATA. The single shared set this hook kept before
 * piece 3 is no longer read and never written. Nor is it deleted:
 * persistPatch spreads `...prior`, so it rides along in storage untouched.
 * Every device's hides therefore start empty on the first load of this
 * build — the one-time clear adopted in
 * docs/decisions/2026-10-15-per-view-hide-clears-saved-sets.md, reached
 * without a version bump.
 *
 * WRITE THE WHOLE hiddenByView. persistPatch replaces top-level keys, so a
 * patch naming one view's array alone — { hiddenByView: { inv: [...] } } —
 * would drop the other view's hides. persistHidden re-reads the stored
 * object and replaces only the current view's array.
 *
 * Unit 5 adds filter / sort / group as SIBLING top-level keys of this same
 * object — `sort` landed in Phase 1 (useFieldSortState.js), filter and group
 * follow. So every write here is a read-modify-write: it merges over whatever
 * is already stored instead of replacing it. A blind setItem would silently
 * drop a rep's saved sort the first time they hid a column. That merge is now
 * persistPatch below, shared with every sibling writer.
 *
 * SCHEMA_VERSION STAYS 1. The Unit 5 keys and hiddenByView are ADDITIVE
 * siblings, not a new shape. Every reader gates on a strict `!==` against
 * its own copy of this constant, and persistPatch stamps THIS copy on every
 * write — so a bump here would also clear, or permanently break, the saved
 * sort, filter and group (see the decision doc above).
 *
 * NEVER THROWS. Private browsing, an exhausted quota, an enterprise policy
 * that disables storage, or a hand-corrupted value all degrade to the same
 * safe state: no hidden fields, this session only. A rep mid-walk loses a
 * preference — never the zone. Each localStorage touch is wrapped on its own,
 * so a read that throws cannot take the write path down with it.
 *
 * THE TOOL ID LOCK. supplier_tool_id can never enter the hidden set, through
 * any entry point, including a value already sitting in storage. It is the
 * row's identity — that is why READONLY_INV_KEYS (editableFields.js) holds
 * it, so TOOL ID survives the INV filter in the record modal, and a grid
 * whose rows cannot be told apart is not a grid. Membership there is NOT the
 * lock: any other read-only row READONLY_INV_KEYS admits stays hideable.
 * The lock is NEVER_HIDDEN, below.
 * Enforced in the hook rather than in the Columns UI so that a second caller,
 * or a stored value written by an older build, cannot route around it.
 */

import { useCallback, useState } from 'react';

const STORAGE_PREFIX = 'toolsight-field-fsg-';
const SCHEMA_VERSION = 1;

/** Keys that may never be hidden. See THE TOOL ID LOCK above. */
export const NEVER_HIDDEN = new Set(['supplier_tool_id']);

/** The views that own a hidden set. Anything else reads empty and never writes. */
const VIEWS = new Set(['inv', 'all']);

const storageKey = (siteId) => `${STORAGE_PREFIX}${siteId}`;

const isPlainObject = (x) => !!x && typeof x === 'object' && !Array.isArray(x);

/**
 * The raw stored object, or null. Returns null for every failure mode —
 * absent key, unparseable JSON, a non-object payload, storage that throws.
 * Version is NOT checked here: the write path needs the sibling keys of even
 * a wrong-version object (see persistPatch), while each read path checks `v`
 * itself.
 *
 * EXPORTED as of Unit 5. useFieldSortState needs the identical raw read — the
 * sort levels live in this same per-site object under a sibling key — and a
 * second copy of these five failure arms is a second thing to get wrong. One
 * reader, one definition of "no usable stored object".
 */
export function readStored(siteId) {
  if (siteId == null) return null;
  try {
    const raw = localStorage.getItem(storageKey(siteId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Coerce any candidate list into a clean Set: strings only, no empties, and
 * never a locked key. Applied on READ as well as on write, so a stored value
 * from an older build that names supplier_tool_id cannot resurrect the lock.
 */
function sanitize(keys) {
  const out = new Set();
  if (!keys) return out;
  for (const k of keys) {
    if (typeof k === 'string' && k !== '' && !NEVER_HIDDEN.has(k)) out.add(k);
  }
  return out;
}

/**
 * The hidden Set for one site and view. Empty Set for no view, an unknown
 * view, or a missing / malformed / wrong-v object. Never reads hiddenFields.
 */
function readHidden(siteId, view) {
  if (!VIEWS.has(view)) return new Set();
  const stored = readStored(siteId);
  if (!stored) return new Set();
  if (stored.v !== SCHEMA_VERSION) return new Set();
  const byView = stored.hiddenByView;
  if (!isPlainObject(byView) || !Array.isArray(byView[view])) return new Set();
  return sanitize(byView[view]);
}

/**
 * Merge `patch` into the site's stored object, preserving every top-level key
 * the caller does not name. Silent on failure — a rep who cannot persist still
 * gets working in-session state, which is strictly better than a crashed
 * records screen.
 *
 * GENERIC AS OF UNIT 5. This was `persist(siteId, next)`, hard-wired to
 * hiddenFields. Unit 5's sort (and later filter/group) are SIBLING keys of the
 * same object — see STORAGE SHAPE above — and each needed the identical
 * read-modify-write. Two writers each doing their own `...prior` spread is how
 * one of them eventually ships a blind setItem and drops the other's key. One
 * writer, one spread, one try/catch.
 *
 * `patch` spreads LAST, so a caller that named `v` would override the version
 * stamp. No caller does and none should: SCHEMA_VERSION is this module's to
 * set, and readHidden/readSort both gate on it with a strict !==.
 *
 * The version is stamped AFTER `...prior` on purpose. A wrong-version object's
 * unknown siblings survive the write (which is why readStored does not check
 * `v`) while the version itself is corrected to what this build writes.
 */
export function persistPatch(siteId, patch) {
  if (siteId == null) return;
  try {
    const prior = readStored(siteId) || {};
    localStorage.setItem(storageKey(siteId), JSON.stringify({
      ...prior,
      v: SCHEMA_VERSION,
      ...patch,
    }));
  } catch {
    /* private browsing, quota, disabled storage — session-only, never throw */
  }
}

/**
 * Persist one view's hidden set and leave every other view's array exactly
 * as stored. THE CLOBBER GUARD: persistPatch replaces top-level keys, so the
 * whole hiddenByView object is rebuilt here — the stored one spread first,
 * then only `view` replaced. The other arrays are copied as stored, not
 * re-sanitized, so a write in INV leaves ALL byte-identical. Never throws:
 * readStored and persistPatch each swallow their own storage failures.
 */
function persistHidden(siteId, view, next) {
  const stored = readStored(siteId);
  const prior = isPlainObject(stored?.hiddenByView) ? stored.hiddenByView : {};
  persistPatch(siteId, { hiddenByView: { ...prior, [view]: [...next] } });
}

export default function useFieldHiddenFields(siteId, view) {
  // The set belongs to a (site, view) pair. When either changes, the render
  // that sees the change re-reads storage and replaces the state BEFORE React
  // commits (state adjusted during render), so the grid never paints one
  // view's columns under the other view's hides, and no mutator can act on
  // the previous pair's set. This replaces the old after-paint site re-read.
  const [state, setState] = useState(() => ({ siteId, view, hidden: readHidden(siteId, view) }));
  let current = state;
  if (state.siteId !== siteId || state.view !== view) {
    current = { siteId, view, hidden: readHidden(siteId, view) };
    setState(current);
  }
  const { hidden } = current;

  // Every mutator is inert without a site or a view: no state change, no
  // write. A write under a literal "toolsight-field-fsg-null" key, or into
  // hiddenByView.undefined, would be a leak, not a fallback.
  const writable = siteId != null && VIEWS.has(view);

  const commit = useCallback((next) => {
    setState({ siteId, view, hidden: next });
    persistHidden(siteId, view, next);
  }, [siteId, view]);

  const toggle = useCallback((key) => {
    if (!writable) return;
    if (typeof key !== 'string' || key === '') return;
    if (NEVER_HIDDEN.has(key)) return;
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    commit(next);
  }, [writable, hidden, commit]);

  // NONE AND ALL ARE SCOPED TO THE KEYS THEY ARE HANDED (hide guard, 7a07a2b).
  // Written when the set was ONE per site and shared by INV and ALL: a bulk
  // button that wrote the whole set wiped the hides of the view the rep was
  // not looking at. Piece 3 gives each view its own set, so that case is gone;
  // the scoping is kept unchanged (Jeff, 2026-10-15) and still leaves alone
  // any stored key the current view does not render, such as a retired field.
  const showAll = useCallback((keys) => {
    if (!writable) return;
    const shown = new Set(Array.isArray(keys) || keys instanceof Set ? keys : []);
    const next = new Set([...hidden].filter((k) => !shown.has(k)));
    commit(next);
  }, [writable, hidden, commit]);

  const hideAll = useCallback((keys) => {
    if (!writable) return;
    const source = Array.isArray(keys) || keys instanceof Set ? keys : [];
    const next = new Set([...hidden, ...sanitize(source)]);
    commit(next);
  }, [writable, hidden, commit]);

  return { hidden, toggle, showAll, hideAll };
}
